// ============================================================================
// The sentences the Chat screen prints, in one file, so a number and the words
// around it can never be assembled two different ways on two different rows.
//
// Nothing here READS anything: every function takes the derived facts the data
// contract already computed (docs/design/handoff/README.md §State Management
// puts scope-size lines and counts under "Derived, never stored") and turns them
// into English. The counting happens on the server side of the contract; this is
// only the grammar.
// ============================================================================

import { daysBetween, type ISODate, type ISOTimestamp } from '@app/shared'
import type { ChatScopeSizePart, ChatThread } from '../../lib/data'
import { clockFromTimestamp } from '../../lib/time'

/**
 * English plurals for the handful of nouns the scope line uses.
 *
 * A map rather than a rule, for the noun that breaks every rule: 'syllabus'. The regular
 * `y → ies` case ('entry', 'journal entry') is handled below it, and everything else takes an -s.
 */
const IRREGULAR: Record<string, string> = { syllabus: 'syllabi' }

export function pluralise(count: number, noun: string): string {
  if (count === 1) return noun
  if (IRREGULAR[noun]) return IRREGULAR[noun]!
  if (noun.endsWith('y')) return `${noun.slice(0, -1)}ies`
  return `${noun}s`
}

/**
 * '4 lectures · 1 syllabus · 3 announcements · 5 journal entries in scope'.
 *
 * Returns null when the scope is empty rather than '0 things in scope' — a scope that states a
 * zero sounds broken where an empty one sounds empty, and the caller has a different sentence for
 * that case.
 */
export function scopeSizeLine(size: ChatScopeSizePart[]): string | null {
  if (size.length === 0) return null
  return `${size.map((p) => `${p.count.toLocaleString('en-US')} ${pluralise(p.count, p.noun)}`).join(' · ')} in scope`
}

/** 'reads 4 transcripts', 'reads 6 entries · 3 memos' — a starter card's promise. */
export function readsLine(size: ChatScopeSizePart[]): string {
  if (size.length === 0) return 'reads nothing yet'
  return `reads ${size.map((p) => `${p.count} ${pluralise(p.count, p.noun)}`).join(' · ')}`
}

/** '4 messages'. */
export const messageCountLine = (n: number) => `${n} ${pluralise(n, 'message')}`

// ── Thread grouping ─────────────────────────────────────────────────────────

export type ThreadGroup = 'TODAY' | 'THIS WEEK' | 'EARLIER'

/**
 * Which heading a thread sits under. Derived from `updatedAt` against the app's own clock — never
 * `Date.now()`, which would make demo mode drift and break every future visual baseline.
 */
export function groupOf(thread: ChatThread, today: ISODate): ThreadGroup {
  const date = thread.updatedAt.slice(0, 10) as ISODate
  if (date === today) return 'TODAY'
  // Yesterday through six days back. `daysBetween(today, date)` is negative in the past.
  return daysBetween(today, date) > -7 ? 'THIS WEEK' : 'EARLIER'
}

export const THREAD_GROUPS: ThreadGroup[] = ['TODAY', 'THIS WEEK', 'EARLIER']

/**
 * 'started 8:04am' — the local wall time the thread's first message was sent.
 *
 * Read off the STRING, never through `new Date()`: the timestamps in this app carry their own
 * offset, and parsing them into a Date would re-render them in whatever timezone the browser
 * happens to be in, which is how a thread started at 8:04am shows up as 3:04pm.
 */
export const startedAtClock = (startedAt: ISOTimestamp) => clockFromTimestamp(startedAt)
